import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetOverlay,
} from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@frontend/shadcn/components/ui/tabs';
import { TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { MemberApplicantOperation } from '@frontend/app/types/globalTypes';
import {
  AtIcon,
  LocationDotIcon,
  UserGroupIcon,
  CircleInfoIcon,
  ChevronDownIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { InstagramIcon, TiktokIcon } from '@revfluence/fresh-icons/brands/esm';
import { UserIcon } from '@revfluence/fresh-icons/regular/esm';
import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { ISocialAccount, TNetworkIdentifier } from '@components';
import { useMemberFieldSchemasQuery } from '@frontend/app/hooks/useMemberFieldSchemasQuery';
import { fetchSocialDataForMember } from '@frontend/app/containers/GuestHome/Groups/GroupCreatorDetail/useFetchSocialDataForMember';
import { backendServerApiEndpoint } from '@frontend/applications/Shared/serviceHosts';
import { useAuth } from '@frontend/context/authContext';
import { useBackendServerFetch } from '@frontend/app/clients/backendServerClient';
import { ApplicantOperationButtons } from '../TableHeader/ApplicantOperationButtons';

// Composable components from the design system
import {
  SheetGrid,
  SheetTopBar,
  SheetColumn,
  PreviewSheetContent,
  SheetHeaderWithClose,
  SheetTopBarActionMenu,
} from '@/shadcn/components/sheet-extensions';
import {
  ProfileSection,
  ProfileAvatar,
  ProfileHandle,
  ProfileMetadataRow,
  ProfileNameRow
} from '@/shadcn/components/profile';
import { SocialStatsSection } from '@/shadcn/components/social-stats';
import { DemographicsSection } from '@/shadcn/components/demographics';

// Local components and hooks
import { TProject } from '../../types';
import { useMemberSocialStats } from './hooks/useMemberSocialStats';
import { useMemberDemographics } from './hooks/useMemberDemographics';
import { SimilarProspects } from './components/SimilarProspects';
import { ProspectPostsGrid } from './components/ProspectPostsGrid';
import { ApplicationTab } from './components/ApplicationTab';
import { EngagementRateSection, useEngagementRateProps } from '@/applications/Social/creator-search/instagram/components/engagement-rate-section';
import { AudienceSummarySection, useAudienceSummaryProps } from '@/applications/Social/creator-search/instagram/components/audience-summary-section';
import { PostPerformanceSection, usePostPerformanceProps } from '@/applications/Social/creator-search/instagram/components/post-performance-section';
import { BrandMentionsSection, useBrandMentionsProps } from '@/applications/Social/creator-search/instagram/components/brand-mentions-section';
import { Notes } from './components/Notes';

const PLATFORM_CONFIGS = {
  instagram: { name: 'Instagram', icon: <InstagramIcon className="w-4 h-4" /> },
  tiktok: { name: 'TikTok', icon: <TiktokIcon className="w-4 h-4" /> },
  youtube: { name: 'YouTube', icon: <div className="w-4 h-4 bg-red-500 rounded text-white text-xs flex items-center justify-center font-bold">YT</div> },
  twitter: { name: 'Twitter', icon: <div className="w-4 h-4 bg-blue-400 rounded text-white text-xs flex items-center justify-center font-bold">TW</div> },
  facebook: { name: 'Facebook', icon: <div className="w-4 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">FB</div> },
  linkedin: { name: 'LinkedIn', icon: <div className="w-4 h-4 bg-blue-700 rounded text-white text-xs flex items-center justify-center font-bold">LI</div> },
  snapchat: { name: 'Snapchat', icon: <div className="w-4 h-4 bg-yellow-400 rounded text-black text-xs flex items-center justify-center font-bold">SC</div> },
  pinterest: { name: 'Pinterest', icon: <div className="w-4 h-4 bg-red-600 rounded text-white text-xs flex items-center justify-center font-bold">PT</div> },
} as const;

interface ProspectsOverviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: MemberSearchQuery_members | null;
  isLoading: boolean;
  socialData?: ISocialAccount | null;
  isFetchingSocialData: boolean;
  project: TProject;
  refetchData: () => void;
  allMembers: MemberSearchQuery_members[];
  /** Optional trigger element rendered outside the Sheet. When provided, clicking it opens the sheet. */
  children?: React.ReactNode;
  usernameProp?: string;
  platformProp?: string;
  showApplicationTab?: boolean;
}

export const ProspectsOverview: React.FC<ProspectsOverviewProps> = ({
  open,
  onOpenChange,
  member,
  isLoading,
  socialData,
  isFetchingSocialData,
  project,
  refetchData,
  children,
  usernameProp,
  platformProp,
  showApplicationTab = false,
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [selectedSocialPlatform, setSelectedSocialPlatform] = useState<string>('instagram');
  const [hasInitializedPlatform, setHasInitializedPlatform] = useState(false);

  // State for managing social data per platform
  const [platformSocialData, setPlatformSocialData] = useState<Record<string, ISocialAccount | null>>({});
  const [isFetchingPlatformData, setIsFetchingPlatformData] = useState(false);

  // Auth and fetch utilities
  const { clientInfo } = useAuth();
  const { backendServerFetchResponse } = useBackendServerFetch();

  useEffect(() => {
    if (!open) {
      setActiveTab(showApplicationTab ? 'summary' : 'social');
    }
  }, [open, showApplicationTab]);

  useEffect(() => {
    setActiveTab(showApplicationTab ? 'summary' : 'social');
  }, [showApplicationTab]);

  // Initialize platform social data with passed socialData prop
  useEffect(() => {
    if (socialData && selectedSocialPlatform) {
      setPlatformSocialData((prev) => {
        // Only set if we don't already have data for this platform
        if (!prev[selectedSocialPlatform]) {
          return {
            ...prev,
            [selectedSocialPlatform]: socialData
          };
        }
        return prev;
      });
    }
  }, [socialData, selectedSocialPlatform]);

  // Get social field schemas
  const { data: { schemas = null } = {} } = useMemberFieldSchemasQuery();

  // Function to fetch social data for a specific platform
  const fetchPlatformSocialData = useCallback(async (platform: string, username: string) => {
    if (!username || !clientInfo?.id || !open) return;

    setIsFetchingPlatformData(true);
    try {
      const response = await fetchSocialDataForMember({
        url: `${backendServerApiEndpoint()}/social_account`,
        clientId: clientInfo.id,
        username,
        network: platform as TNetworkIdentifier,
        include_social_profile_data: true,
        backendServerFetchResponse,
      });

      if (response?.data?.length) {
        const newSocialData = response.data[0] as ISocialAccount;
        setPlatformSocialData((prev) => ({
          ...prev,
          [platform]: newSocialData
        }));
      }
    } catch (error) {
      setPlatformSocialData((prev) => ({
        ...prev,
        [platform]: null
      }));
    } finally {
      setIsFetchingPlatformData(false);
    }
  }, [backendServerFetchResponse, clientInfo?.id, open]);

  // Get the active social data based on selected platform (memoized)
  const activeSocialData = useMemo((): ISocialAccount | null => {
    // First priority: use platform-specific data if available
    const platformData = platformSocialData[selectedSocialPlatform];
    if (platformData) {
      return platformData;
    }

    // Second priority: use passed socialData prop (for initial load)
    if (socialData) {
      return socialData;
    }

    if (platformProp && usernameProp) {
      fetchPlatformSocialData(platformProp, usernameProp);
    }
  }, [platformSocialData, selectedSocialPlatform, socialData, platformProp, usernameProp, fetchPlatformSocialData]);

  const isActiveSocialDataLoading = isFetchingSocialData || isFetchingPlatformData;

  // Success handler for ApplicantOperationButtons
  const handleOperationSuccess = (_: number[], operation?: MemberApplicantOperation) => {
    const actionText = operation === MemberApplicantOperation.Approve
      ? 'approved'
      : operation === MemberApplicantOperation.Maybe
      ? 'marked as maybe'
      : 'rejected';

    toast({
      title: 'Success',
      description: `${member?.name || member?.email} has been ${actionText}.`,
    });
    refetchData();
  };

  // Get social stats, engagement rate, audience summary, post performance, brand mentions, and demographics using our hooks
  const socialStatsProps = useMemberSocialStats(member, activeSocialData, isActiveSocialDataLoading);
  const engagementRateProps = useEngagementRateProps(isActiveSocialDataLoading, activeSocialData);
  const audienceSummaryProps = useAudienceSummaryProps(isActiveSocialDataLoading, activeSocialData);
  const postPerformanceProps = usePostPerformanceProps(isActiveSocialDataLoading, activeSocialData);
  const brandMentionsProps = useBrandMentionsProps(isActiveSocialDataLoading, activeSocialData);
  const demographicsProps = useMemberDemographics(member, activeSocialData, isActiveSocialDataLoading);

  // Extract display data
  const username = activeSocialData?.username || 'unknown';
  const fullName = activeSocialData?.full_display_name || member?.name || '';
  const email = member?.email || '';
  const profilePictureUrl = activeSocialData?.profile_picture || member?.profilePicture;
  const followers = activeSocialData?.follower_count || 0;
  const location = activeSocialData?.location_name || activeSocialData?.country_code;

    // Platform configurations
  const getPlatformConfig = (platform: string) => {
    return PLATFORM_CONFIGS[platform as keyof typeof PLATFORM_CONFIGS] || { name: platform, icon: <div className="w-4 h-4 bg-grey-4 rounded text-white text-xs flex items-center justify-center font-bold">?</div> };
  };

  // Extract social accounts from member.fields using schemas
  const getSocialAccounts = useCallback(() => {
    if (!member?.fields || !schemas) return [];

    const accounts = [];

    // Get all social media schemas
    const socialSchemas = schemas.filter((schema) => {
      const name = schema.name?.toLowerCase() || '';
      return ['instagram', 'tiktok', 'youtube', 'twitter', 'facebook', 'linkedin', 'snapchat', 'pinterest'].includes(name);
    });

    for (const schema of socialSchemas) {
      const schemaId = schema.id;
      const platformName = schema.name?.toLowerCase() || '';
      const username = member.fields[schemaId];

      if (username && typeof username === 'string' && username.trim()) {
        const config = getPlatformConfig(platformName);
        accounts.push({
          platform: platformName,
          platformName: config.name,
          platformIcon: config.icon,
          username: username.trim(),
          schemaId,
          // Note: We don't have profile picture or follower count in member.fields
          // These would come from the social data API
          profilePicture: null,
          followerCount: 0,
          isActive: platformName === selectedSocialPlatform,
        });
      }
    }

    return accounts;
  }, [member?.fields, schemas, selectedSocialPlatform]);

  // Set initial selected platform based on available social accounts (only on first load)
  useEffect(() => {
    if (member?.fields && schemas && !hasInitializedPlatform) {
      const accounts = getSocialAccounts();
      if (accounts.length > 0) {
        // Set the first available platform as selected
        const firstPlatform = accounts[0].platform;
        setSelectedSocialPlatform(firstPlatform);
        setHasInitializedPlatform(true);
      }
    }
  }, [member?.fields, schemas, getSocialAccounts, hasInitializedPlatform]);

  const socialAccounts = getSocialAccounts();
  const activeSocialAccount = socialAccounts.find((acc) => acc.isActive) || socialAccounts[0];

  // Use active social account data if available, fallback to current logic
  const displayUsername = activeSocialAccount?.username || username;
  const displayProfilePicture = activeSocialAccount?.profilePicture || profilePictureUrl;

  // Only return null when the sheet should close (not open)
  if (!open) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetOverlay className="z-[190]" />
      <SheetContent
        className="w-full overflow-y-auto p-0 z-[200] sm:max-w-[1000px] [&>button:first-of-type]:hidden flex flex-col"
        side="right"
      >
        <TooltipProvider>
          <div className="flex flex-col h-full flex-1">
            <SheetHeaderWithClose
              title="Prospect Overview"
              onClose={() => onOpenChange(false)}
              closeAriaLabel="Close overview"
            />

            {/* Top Bar */}
            <SheetTopBar>
              <div className="flex items-center gap-2">
                {activeSocialData ? (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="px-3 py-1 bg-white rounded-lg border border-grey-2 flex items-center gap-1 h-auto"
                        >
                          <div className="flex items-center gap-1">
                            <img className="w-3 h-3 rounded-sm" src={displayProfilePicture || 'https://placehold.co/12x12'} alt="" />
                            <div className="text-grey-6 text-sm font-medium">{displayUsername}</div>
                            {socialAccounts.length > 1 && (
                              <Badge variant="secondary" className="ml-1 px-1 py-0 text-xs">
                                +{socialAccounts.length - 1}
                              </Badge>
                            )}
                          </div>
                          <ChevronDownIcon className="w-3 h-3 text-grey-6" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-64">
                        {socialAccounts.length > 0 ? (
                          socialAccounts.map((account) => (
                            <DropdownMenuItem
                              key={account.platform}
                              className={`flex items-center gap-3 p-3 cursor-pointer ${
                                account.isActive ? 'bg-blue-1' : ''
                              }`}
                              onClick={() => {
                                setSelectedSocialPlatform(account.platform);
                                // Fetch social data for the selected platform if not already cached
                                if (!platformSocialData[account.platform]) {
                                  fetchPlatformSocialData(account.platform, account.username);
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                {account.platformIcon}
                                <span className="text-sm font-medium text-grey-7">
                                  {account.username}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))
                        ) : (
                          <DropdownMenuItem disabled className="text-grey-5 text-sm">
                            No social accounts found
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Badge className="px-2 py-0.5 bg-blue-4 rounded-lg flex items-center gap-1 text-white">
                      <CircleInfoIcon className="w-3 h-3" />
                      <span className="text-xs font-normal">Last 30 Days</span>
                    </Badge>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-6 rounded" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                )}
              </div>

              {/* Action Menu */}
              {showApplicationTab && (
                <SheetTopBarActionMenu
                  disabled={isLoading}
                >
                  <ApplicantOperationButtons
                    disabled={isLoading}
                    memberIds={member ? [member.id] : []}
                    project={project}
                    showFlexUI
                    hideApplicantCount
                    onMemberApplicantOperationSuccess={handleOperationSuccess}
                  />
                </SheetTopBarActionMenu>
              )}
            </SheetTopBar>

            <SheetGrid>
              {/* Left column with prospect profile and recent posts */}
              <SheetColumn className="md:col-span-1 flex flex-col h-full">
                <PreviewSheetContent className="flex-1">
                  {/* Profile Section */}
                  <ProfileSection>
                    {(isLoading || isActiveSocialDataLoading) ? (
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-16 w-16 rounded-full" />
                        <div className="flex flex-col gap-1 flex-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-36" />
                          <Skeleton className="h-3 w-40" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <ProfileAvatar
                          src={profilePictureUrl ?? undefined}
                          alt={fullName || username}
                          name={fullName || username}
                          size="h-16 w-16"
                        />
                        <div className="flex flex-col">
                          <ProfileHandle
                            handle={username}
                            platform={selectedSocialPlatform}
                            onProfileClick={() => {
                              if (activeSocialData?.username) {
                                const platformUrls = {
                                  instagram: `https://www.instagram.com/${activeSocialData.username}`,
                                  tiktok: `https://www.tiktok.com/@${activeSocialData.username}`,
                                  youtube: `https://www.youtube.com/@${activeSocialData.username}`,
                                  twitter: `https://twitter.com/${activeSocialData.username}`,
                                  facebook: `https://www.facebook.com/${activeSocialData.username}`,
                                  linkedin: `https://www.linkedin.com/in/${activeSocialData.username}`,
                                };
                                const url = platformUrls[selectedSocialPlatform as keyof typeof platformUrls] || `https://www.instagram.com/${activeSocialData.username}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                          />
                          <ProfileNameRow
                            icon={<UserIcon className="h-3 w-3 text-grey-5" />}
                            name={fullName}
                            fallback="--"
                          />
                          <ProfileMetadataRow
                            icon={<AtIcon className="h-3 w-3 text-grey-5" />}
                            truncate
                            tooltipContent={email}
                          >
                            {email || '--'}
                          </ProfileMetadataRow>
                          <div className="flex items-center gap-4">
                            <ProfileMetadataRow icon={<UserGroupIcon className="h-3 w-3 text-grey-5" />}>
                              {followers.toLocaleString()}
                            </ProfileMetadataRow>
                            {location && (
                              <ProfileMetadataRow icon={<LocationDotIcon className="h-3 w-3 text-grey-5" />}>
                                {location}
                              </ProfileMetadataRow>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </ProfileSection>

                  {/* Posts Grid */}
                  <ProspectPostsGrid
                    posts={
                      (activeSocialData?.popular_posts?.length ? activeSocialData.popular_posts : null) ||
                      ((activeSocialData as any)?.historic_stats?.top_posts?.length ? (activeSocialData as any).historic_stats.top_posts : null) ||
                      ((activeSocialData as any)?.top_sponsored_posts?.length ? (activeSocialData as any).top_sponsored_posts : null) ||
                      (activeSocialData?.featured_posts?.length ? activeSocialData.featured_posts : null) ||
                      (activeSocialData?.sponsored_posts?.length ? activeSocialData.sponsored_posts : null) ||
                      (activeSocialData?.posts?.length ? activeSocialData.posts : null) ||
                      []
                    }
                    isLoading={isActiveSocialDataLoading}
                    username={username}
                  />
                </PreviewSheetContent>
              </SheetColumn>

              {/* Right column – tabs content */}
              <SheetColumn className="md:col-span-2">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <div className="border-b mt-3">
                    <TabsList className="border-0 bg-transparent m-0">
                      {showApplicationTab && (
                        <TabsTrigger
                          value="summary"
                          className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                        >
                          Application
                        </TabsTrigger>
                      )}
                      <TabsTrigger
                        value="social"
                        className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                      >
                        Social Metrics
                      </TabsTrigger>
                      <TabsTrigger
                        value="similar"
                        className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                      >
                        Similar Creators
                      </TabsTrigger>
                      <TabsTrigger
                        value="notes"
                        className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                      >
                        Notes
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  {showApplicationTab && (
                    <TabsContent value="summary" className="mt-4">
                      <ApplicationTab
                        member={member}
                        project={project}
                        isLoading={isLoading || isFetchingSocialData}
                      />
                    </TabsContent>
                  )}

                  <TabsContent value="social" className="mt-4">
                    <div className="space-y-8">
                      <div>
                        <SocialStatsSection {...socialStatsProps} />
                      </div>
                      <div>
                        <EngagementRateSection {...engagementRateProps} />
                      </div>
                      <div>
                        <AudienceSummarySection {...audienceSummaryProps} />
                      </div>
                      <div>
                        <PostPerformanceSection {...postPerformanceProps} />
                      </div>
                      <div>
                        <DemographicsSection {...demographicsProps} />
                      </div>
                      <div>
                        <BrandMentionsSection {...brandMentionsProps} />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="similar" className="mt-4">
                    <SimilarProspects
                      socialData={activeSocialData}
                      onProspectClick={(prospect) => {
                        // Handle navigation to similar prospect
                        console.log('Navigate to prospect:', prospect);
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="notes" className="mt-4">
                    <Notes memberId={member?.id} />
                  </TabsContent>
                </Tabs>
              </SheetColumn>
            </SheetGrid>
          </div>
        </TooltipProvider>
      </SheetContent>
    </Sheet>
  );
};

export default ProspectsOverview;
